import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Avatar, RichTextValue, TextWithLineBreaks } from 'components/common';
import { BoxRow } from 'components/common/box';
import {
  AchievementItemAddedBy,
  AchievementCompletionsFillTemplateActions,
  AchievementCompletionsFillTemplateAdd
} from '.';

import type { AchievementProgress } from 'shared-types';

interface Props {
  achievement?: {
    id: number;
    isOwnerOrSuperadmin?: boolean;
    isSupervisor?: boolean;
    userAdminPermissions?: string[];
  };
  adminMode?: boolean;
  canMarkComplete: boolean;
  item: AchievementProgress['template'][number];
  itemIndex: number;
  targetUserId: number;
  updateItems?: () => Promise<void>;
}

const AchievementCompletionsFillTemplateTasklist = ({
  achievement,
  adminMode = false,
  canMarkComplete,
  item,
  itemIndex,
  targetUserId,
  updateItems
}: Props) => {
  const { messages, theme } = useReduxState();

  const answer = item.answers[0];

  return (
    <BoxRow>
      <div className="content">
        <div className="status">
          <Avatar
            size="24px"
            {...(item.isCompleted
              ? {
                  backgroundColor: theme.successColor,
                  color: theme.successTextColor,
                  fontSize: '1rem',
                  icon: 'check',
                  padding: '6px 0'
                }
              : {
                  fontSize: '1.4rem',
                  padding: '2px 0'
                })}
          >
            {item.isCompleted ? null : itemIndex}
          </Avatar>
        </div>

        <div className="item">
          <div className="item-inner">
            <div className="item-text">
              <RichTextValue html={item.question} />
            </div>

            {answer?.text ? (
              <div className="comment">
                <TextWithLineBreaks text={answer.text} />
              </div>
            ) : null}

            {answer?.user ? (
              <div className="marked">
                <AchievementItemAddedBy
                  addedByString={`${getMessageWithPlaceholders({
                    message: messages.achievementUsersTemplateMarkedBy,
                    placeholders: { user: '' }
                  })}`}
                  date={answer.date}
                  enableUserLink={adminMode}
                  user={answer.user}
                />
              </div>
            ) : canMarkComplete ? (
              <div className="add">
                <AchievementCompletionsFillTemplateAdd
                  achievementId={achievement?.id}
                  actionText={messages.achievementUsersTemplateMarkTaskComplete}
                  buttonText={messages.achievementUsersTemplateMarkTaskComplete}
                  confirmText={
                    messages.achievementUsersTemplateMarkTaskCompleteConfirm
                  }
                  confirmTitle={
                    messages.achievementUsersTemplateMarkTaskComplete
                  }
                  successMessage={
                    messages.achievementUsersTemplateMarkTaskCompleteSuccess
                  }
                  targetUserId={targetUserId}
                  templateItemId={item.id}
                  textInfoMessage={
                    adminMode
                      ? messages.achievementUsersTemplateCommentInfo
                      : undefined
                  }
                  textPlaceholder={`${messages.commonAddComment} (${messages.commonOptional})`}
                  updateUserAchievement={updateItems}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="actions">
          <AchievementCompletionsFillTemplateActions
            achievement={achievement}
            canEdit={canMarkComplete && item.isCompleted}
            deleteActionText={messages.achievementUsersTemplateRemoveTaskItem}
            deleteButtonText={messages.achievementUsersTemplateRemoveTaskItem}
            deleteSuccessMessage={
              messages.achievementUsersTemplateRemoveTaskItemSuccess
            }
            deleteText={messages.achievementUsersTemplateRemoveTaskItemConfirm}
            deleteTitle={messages.achievementUsersTemplateRemoveTaskItem}
            editSuccessMessage={
              messages.achievementUsersTemplateEditTaskItemSuccess
            }
            editTextInfoMessage={
              adminMode
                ? messages.achievementUsersTemplateCommentInfo
                : undefined
            }
            editTextLabel={messages.commonComment}
            editTitle={messages.achievementUsersTemplateEditTaskItem}
            id={item.id}
            targetUserId={targetUserId}
            updateUserAchievement={updateItems}
            text={answer?.text}
          />
        </div>

        <style jsx>{`
          .content {
            align-items: flex-start;
            display: flex;
            padding: 5px 0;
          }
          .status {
            flex-grow: 0;
            flex-shrink: 0;
            margin-right: 15px;
          }
          .item {
            flex-grow: 1;
            flex-shrink: 1;
            margin-right: 30px;
            padding: 1px 0;
          }
          .item-inner {
            max-width: 720px;
          }
          .item-text {
            color: ${theme.textColorLight};
            font-size: 1.5rem;
            font-weight: 600;
            line-height: 1.4;
          }
          .comment {
            background-color: ${theme.lightGreyLighter};
            border-radius: 7px;
            color: ${theme.textColorLight};
            font-size: 1.4rem;
            font-weight: 400;
            margin-top: 10px;
            padding: 10px;
          }
          .marked {
            margin-top: 10px;
          }
          .add {
            margin: 15px 0 5px;
          }
          .actions {
            flex-grow: 0;
            flex-shrink: 0;
          }
        `}</style>
      </div>
    </BoxRow>
  );
};

export default AchievementCompletionsFillTemplateTasklist;
